export function formatAmount(value) {
    return Number(value).toLocaleString('en-IN', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

// A blank box is "not entered yet", not zero - no check runs on it.
function amountOf(value) {
    if (value === undefined || value === null || value === '') return null;
    const number = Number(value);
    return Number.isNaN(number) ? null : number;
}

/**
 * Figures on this step that contradict each other.
 *
 * Each check is arithmetic between boxes on this screen, never a tax judgement, and
 * runs only when every box it reads is filled. They warn and do not block: the CA
 * decides which box is wrong and corrects it. BGSS asked for this after a
 * transaction went out with Remittance after TDS at 0 and nothing on screen said so.
 *
 * The tolerance is one unit of the rounding in use (1, or 0.01 with two-decimal
 * rounding): the calculation rounds each figure on its own, so a sum of rounded
 * figures can be one unit off a rounded sum, and that is not an error. Manual
 * entries get the wider tolerance, since there is no rounding scheme to go by.
 */
export function getFigureWarnings(values, total) {
    const warnings = [];
    const system = values.is_system_calculation_applicable === 'Y';
    const taxable = values.is_it_taxable === 'Y';
    const unit = system && Number(values.rounding) === 2 ? 0.01 : 1;
    const off = (a, b, tolerance = unit) => Math.abs(a - b) > tolerance * 1.01;

    const payableFrgn = amountOf(values.amt_payable_frgn);
    const payableInr = amountOf(values.amt_payable_inr);
    const exchangeRate = amountOf(values.exchange_rate);
    const reimbursement = amountOf(values.expenses_reimbursement_frgn) || 0;
    // Not taxable means no tax is deducted; the TDS boxes are hidden then and may
    // hold whatever was typed before the answer changed, so they are not read.
    const tdsFrgn = taxable ? amountOf(values.tds_amt_frgn) : 0;
    const tdsInr = taxable ? amountOf(values.tds_amt_inr) : 0;
    const tdsRate = amountOf(values.tds_rate);
    const afterFrgn = amountOf(values.actual_amt_after_tds_frgn);
    const afterInr = amountOf(values.actual_amt_after_tds_inr);

    // The calculation fills these from the invoices and the reimbursement. If they
    // no longer agree, something was changed after Get Computation was pressed.
    // Not checked when grossed up - the amount payable is then the grossed-up figure.
    if (system && values.is_grossed_up !== 'Y' && total > 0 && payableFrgn !== null
        && off(payableFrgn, total - reimbursement)) {
        warnings.push({
            title: 'The computed figures are out of date',
            detail: `Amount Payable (Foreign Currency) is ${formatAmount(payableFrgn)}, but the invoices `
                + `less reimbursement come to ${formatAmount(total - reimbursement)}. An invoice amount or `
                + `the Expense Reimbursement was changed after Get Computation was pressed. `
                + `Press Get Computation again.`
        });
    }

    const afterLabel = taxable ? 'Amount Payable less TDS' : 'Amount Payable (no tax is deducted, as the remittance is not chargeable to tax)';
    if (payableFrgn !== null && tdsFrgn !== null && afterFrgn !== null && off(afterFrgn, payableFrgn - tdsFrgn)) {
        warnings.push({
            title: 'Actual Amount after TDS (Foreign Currency) does not add up',
            detail: `It is ${formatAmount(afterFrgn)}. It should equal ${afterLabel}: `
                + `${formatAmount(payableFrgn - tdsFrgn)}.`
        });
    }
    if (payableInr !== null && tdsInr !== null && afterInr !== null && off(afterInr, payableInr - tdsInr, 1)) {
        warnings.push({
            title: 'Actual Amount after TDS (India Currency) does not add up',
            detail: `It is ${formatAmount(afterInr)}. It should equal ${afterLabel}: `
                + `${formatAmount(payableInr - tdsInr)}.`
        });
    }

    if (taxable && payableFrgn !== null && tdsFrgn !== null && tdsRate !== null
        && off(tdsFrgn, payableFrgn * tdsRate / 100)) {
        warnings.push({
            title: 'TDS Amount does not match the TDS Rate',
            detail: `TDS Amount (Foreign Currency) is ${formatAmount(tdsFrgn)}. Amount Payable `
                + `${formatAmount(payableFrgn)} at ${tdsRate}% comes to ${formatAmount(payableFrgn * tdsRate / 100)}. `
                + `Correct the TDS Amount or the TDS Rate.`
        });
    }

    // Each rupee figure is converted from the unrounded foreign amount, so allow the
    // foreign rounding times the rate, plus a rupee.
    if (payableFrgn !== null && payableInr !== null && exchangeRate !== null
        && off(payableInr, payableFrgn * exchangeRate, (unit / 2) * exchangeRate + 1)) {
        warnings.push({
            title: 'Amount Payable in Indian Currency does not match the exchange rate',
            detail: `Amount Payable (Indian Currency) is ${formatAmount(payableInr)}. `
                + `${formatAmount(payableFrgn)} at ${exchangeRate} comes to ${formatAmount(payableFrgn * exchangeRate)}. `
                + `Correct the amount or the Exchange Rate.`
        });
    }

    return warnings;
}
